import React from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(_error) {
    // Atualiza o state para mostrar a UI de fallback
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    // Log do erro para monitoramento
    console.error('ErrorBoundary capturou um erro:', error, errorInfo)
    
    this.setState({
      error,
      errorInfo
    })

    // Aqui você pode integrar com Sentry ou outro serviço de monitoramento
    if (import.meta.env.VITE_APP_ENV === 'production') {
      // Exemplo: Sentry.captureException(error)
    }
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      // UI de fallback customizada
      return (
        <div className="min-h-screen bg-secondary-50 flex items-center justify-center px-4">
          <div className="max-w-lg w-full text-center">
            <div className="bg-white rounded-xl shadow-medium p-8">
              {/* Ícone de erro */}
              <div className="flex justify-center mb-6">
                <div className="bg-red-100 rounded-full p-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
              </div>

              {/* Título e mensagem */}
              <h1 className="text-2xl font-bold text-secondary-900 mb-4">
                Ops! Algo deu errado
              </h1>
              
              <p className="text-secondary-600 mb-8">
                Ocorreu um erro inesperado. Nossa equipe foi notificada e está 
                trabalhando para resolver o problema.
              </p>

              {/* Botões de ação */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={this.handleReload}
                  className="btn btn-primary flex items-center gap-2 justify-center"
                >
                  <RefreshCw className="w-4 h-4" />
                  Tentar novamente
                </button>
                
                <button
                  onClick={this.handleGoHome}
                  className="btn btn-outline flex items-center gap-2 justify-center"
                >
                  <Home className="w-4 h-4" />
                  Ir para início
                </button>
              </div>

              {/* Detalhes do erro (apenas em desenvolvimento) */}
              {import.meta.env.VITE_APP_ENV === 'development' && this.state.error && (
                <details className="mt-8 text-left">
                  <summary className="cursor-pointer text-sm text-secondary-500 hover:text-secondary-700">
                    Detalhes técnicos (desenvolvimento)
                  </summary>
                  <div className="mt-4 p-4 bg-secondary-100 rounded-lg text-xs font-mono text-secondary-700 overflow-auto max-h-48">
                    <div className="mb-2">
                      <strong>Erro:</strong> {this.state.error.toString()}
                    </div>
                    <div>
                      <strong>Stack Trace:</strong>
                      <pre className="whitespace-pre-wrap mt-1">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  </div>
                </details>
              )}
            </div>

            {/* Informações de contato */}
            <div className="mt-6 text-sm text-secondary-500">
              <p>
                Precisa de ajuda imediata? Entre em contato conosco em{' '}
                <a 
                  href="mailto:suporte@temporiza.com" 
                  className="text-primary-600 hover:text-primary-700"
                >
                  suporte@temporiza.com
                </a>
              </p>
            </div>
          </div>
        </div>
      )
    }

    // Se não há erro, renderiza os children normalmente
    return this.props.children
  }
}

// Componente funcional alternativo para casos simples
export const SimpleErrorFallback = ({ error, resetError }) => {
  return (
    <div className="min-h-96 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="flex justify-center mb-4">
          <AlertTriangle className="w-12 h-12 text-red-500" />
        </div>
        
        <h2 className="text-xl font-semibold text-secondary-900 mb-2">
          Erro ao carregar componente
        </h2>
        
        <p className="text-secondary-600 mb-6">
          {error?.message || 'Ocorreu um erro inesperado'}
        </p>
        
        <button
          onClick={resetError}
          className="btn btn-primary"
        >
          Tentar novamente
        </button>
      </div>
    </div>
  )
}

export default ErrorBoundary
